// =============================================================
// Chart colours -- the maths, and nothing else.
//
// Pure: no React, no Chart.js, no DOM. utils/chartTheme.js wraps it
// in a hook; this file exists on its own so the exact code that ships
// can be run against the dataviz palette validator (see
// instruction_ui.json "charts" for how that was done and what passed).
//
// Colour distance is OKLab Euclidean x100, and colour-vision deficiency
// is simulated with Machado-Oliveira-Fernandes 2009 at severity 1.0 --
// the same model and units the validator uses, so a number worked out
// here and a number the validator prints are the same number.
// =============================================================

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const hexToRgb = (hex) => {
  let h = String(hex || '').trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toLinear = (c) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

const toByte = (c) => {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(clamp(v, 0, 1) * 255);
};

const linearToHex = (rgb) =>
  `#${rgb.map((c) => toByte(c).toString(16).padStart(2, '0')).join('').toUpperCase()}`;

const linearOf = (hex) => (hexToRgb(hex) || [0, 0, 0]).map(toLinear);

const linearToOklab = ([r, g, b]) => {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
};

const oklabToLinear = ([L, a, b]) => {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
};

const inGamut = (lab) => oklabToLinear(lab).every((c) => c >= -1e-4 && c <= 1 + 1e-4);

// Protanopia and deuteranopia, severity 1.0, applied in linear RGB.
const CVD = [
  [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]],
  [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.01182, 0.04294, 0.968881]],
];

const simulate = (rgb, M) =>
  M.map((row) => clamp(row[0] * rgb[0] + row[1] * rgb[1] + row[2] * rgb[2], 0, 1));

const distance = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) * 100;

/** Normal-vision distance between two colours, OKLab x100. */
export const deltaE = (a, b) => distance(linearToOklab(linearOf(a)), linearToOklab(linearOf(b)));

/** The worse of the protan and deutan distances, OKLab x100. */
export const deltaECvd = (a, b) =>
  Math.min(
    ...CVD.map((M) =>
      distance(linearToOklab(simulate(linearOf(a), M)), linearToOklab(simulate(linearOf(b), M))),
    ),
  );

/** WCAG relative luminance and contrast. */
const luminance = (hex) => {
  const [r, g, b] = linearOf(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

// ── the gates ──────────────────────────────────────────────────
// The validator's: a series colour sits in the light-mode lightness band,
// clears the chroma floor, and holds 3:1 against the chart surface. Two
// series are told apart at normal-vision dE >= 15 and CVD dE >= 8.
const L_MIN = 0.45;
const L_MAX = 0.68;
const C_FLOOR = 0.105;
const MARK_CONTRAST = 3;
const NORMAL_FLOOR = 15;
const CVD_TARGET = 8;

export const separable = (a, b) => deltaE(a, b) >= NORMAL_FLOOR && deltaECvd(a, b) >= CVD_TARGET;

/**
 * The palette's accent, made fit to be a chart mark.
 *
 * The accent is chosen for buttons, and a button colour is not always a
 * chart colour: Calm's teal sits under the chroma floor and reads grey
 * beside the other series, and a custom sky or yellow can fall below 3:1
 * on the card. So the HUE is kept -- a chart on the Warm palette is still
 * visibly the warm palette -- and lightness and chroma are moved just far
 * enough to pass. Classic, Premium and Warm pass as they are and come
 * back unchanged.
 */
export const chartAccent = (accentHex, surface = '#FFFFFF') => {
  if (!hexToRgb(accentHex)) return '#3B6FF0';
  const [L0, a0, b0] = linearToOklab(linearOf(accentHex));
  const C0 = Math.hypot(a0, b0);
  const hue = Math.atan2(b0, a0);
  const at = (L, C) => [L, C * Math.cos(hue), C * Math.sin(hue)];

  const passes = (hex) => {
    const [L, a, b] = linearToOklab(linearOf(hex));
    return L >= L_MIN - 1e-3 && L <= L_MAX + 1e-3 && Math.hypot(a, b) >= C_FLOOR - 2e-3 && contrast(hex, surface) >= MARK_CONTRAST;
  };
  if (passes(accentHex)) return linearToHex(linearOf(accentHex));

  // The nearest lightness in the band, to the accent's own, where the
  // floor chroma fits inside sRGB. Cyan-ish hues cannot carry much chroma
  // at mid lightness, so the search walks outward rather than clamping.
  const base = clamp(L0, L_MIN, L_MAX);
  const wantC = Math.max(C0, C_FLOOR);
  const tries = [base];
  for (let d = 0.01; d <= L_MAX - L_MIN; d += 0.01) tries.push(base - d, base + d);

  let best = null;
  for (const L of tries.filter((x) => x >= L_MIN && x <= L_MAX)) {
    let C = wantC;
    while (C > 0.02 && !inGamut(at(L, C))) C -= 0.002;
    const candidate = at(L, C);
    if (C >= C_FLOOR - 1e-6) {
      best = candidate;
      break;
    }
    if (!best || C > Math.hypot(best[1], best[2])) best = candidate;
  }

  // Marks need 3:1 on the surface: darken, hue held, until they have it.
  let [L, a, b] = best;
  let hex = linearToHex(oklabToLinear(best));
  while (contrast(hex, surface) < MARK_CONTRAST && L > 0.3) {
    L -= 0.01;
    let lab = [L, a, b];
    let k = 1;
    while (!inGamut(lab) && k > 0.2) {
      k -= 0.02;
      lab = [L, a * k, b * k];
    }
    hex = linearToHex(oklabToLinear(lab));
  }
  return hex;
};

/* The fixed hues slots 2..4 are drawn from, in order: four literals already
   in the project's palette (instruction_ui.json "palette.accent"), a
   magenta, and a deep rose. The deep rose is there for the accents that
   rule out several of the others at once -- a blue accent rules out violet
   and sky, a teal one rules out teal and sky -- so that every accent still
   gets four.

   Red, gold and the status tints are NOT here: they are reserved for
   meaning. Gold #B9700C was in this list and came out -- it is the warn
   pill's own colour and sits 4.8 from the attendance "fair" band. */
export const SERIES_POOL = ['#0E9C72', '#E8590C', '#5B4FE0', '#1D91DA', '#C2338A', '#9D174D'];

/** The most series a chart may colour. Past this: "Other", facets, or a table. */
export const MAX_SERIES = 4;

/**
 * Slot 1 is the palette's accent (as a chart mark); slots 2..4 come from
 * SERIES_POOL, in order, each one distinguishable from EVERY slot already
 * taken -- not only from the slot before it. Lines cross and legend
 * swatches sit side by side, so any two series can meet. An adjacency-only
 * rule was tried first and let a four-line chart put the accent's own hue
 * back in slot 4 (an orange accent drew two lines 0.4 apart); the shipped
 * Classic set had the same flaw, blue #3B6FF0 beside violet #5B4FE0 at
 * normal-vision dE 7.7.
 *
 * The skip is why a slot's hue can differ between palettes -- but within a
 * palette it is fixed, so a series keeps its colour when others are
 * filtered away. Verified to yield four for every prebuilt palette and for
 * custom accents around the wheel (instruction_ui.json "charts").
 */
export const deriveSeries = (accentHex, surface = '#FFFFFF') => {
  const series = [chartAccent(accentHex, surface)];
  for (const c of SERIES_POOL) {
    if (series.length >= MAX_SERIES) break;
    if (series.every((s) => separable(s, c))) series.push(c);
  }
  // Never padded with a hue that fails the gates. chartTheme fills any slot
  // this could not with muted grey, which reads as "the rest" rather than
  // as another identity.
  return series;
};

/** White or ink for text set INSIDE a filled mark, by which one reads. */
export const onFill = (fillHex, ink = '#101828') =>
  contrast('#FFFFFF', fillHex) >= 4.5 ? '#FFFFFF' : ink;

/** A colour at an alpha, for an area wash. Canvas cannot use color-mix(). */
export const wash = (hex, alpha = 0.1) => {
  const rgb = hexToRgb(hex) || [59, 111, 240];
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};
